/**
 * sessionPersistence.ts — Cross-page localStorage-backed persistence
 *
 * All reservoir/diffuser design data is mirrored to BOTH sessionStorage
 * (for backward compatibility with existing pages) AND localStorage
 * (so it survives browser restarts and is shared across tabs).
 *
 * Pages should call `savePersisted(key, value)` whenever the design changes,
 * and `loadPersisted(key, defaults)` on mount.
 *
 * A custom event `rb-persist-changed` is dispatched on save, so any page
 * showing derived data can subscribe and re-render.
 */

const STORAGE_PREFIX = 'rb_';        // namespace
const VERSION = 'v1';                 // bump if schema changes incompatibly
const LOCAL_KEYSET = 'rb_persist_keys'; // master list of keys touched

const isBrowser = typeof window !== 'undefined';

function getAll(): string[] {
  if (!isBrowser) return [];
  try {
    const raw = localStorage.getItem(LOCAL_KEYSET);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch { return []; }
}

function trackKey(key: string) {
  if (!isBrowser) return;
  try {
    const keys = new Set(getAll());
    keys.add(key);
    localStorage.setItem(LOCAL_KEYSET, JSON.stringify([...keys]));
  } catch { /* quota or private mode */ }
}

/**
 * Save a value to BOTH sessionStorage and localStorage, then notify listeners.
 */
export function savePersisted<T>(key: string, value: T): void {
  if (!isBrowser) return;
  const serial = JSON.stringify(value);
  try {
    sessionStorage.setItem(key, serial);
  } catch { /* session storage full or disabled */ }
  try {
    localStorage.setItem(key, serial);
    localStorage.setItem(`${key}__ts`, new Date().toISOString());
    trackKey(key);
  } catch { /* quota or private mode */ }
  try {
    window.dispatchEvent(new CustomEvent('rb-persist-changed', { detail: { key, value } }));
  } catch { /* ignore */ }
}

/**
 * Load a persisted value. Reads from localStorage first (most persistent),
 * falls back to sessionStorage, then to defaults.
 */
export function loadPersisted<T>(key: string, defaults: T): T {
  if (!isBrowser) return defaults;
  try {
    const local = localStorage.getItem(key);
    if (local) return { ...(defaults as object), ...JSON.parse(local) } as T;
  } catch { /* parse error */ }
  try {
    const session = sessionStorage.getItem(key);
    if (session) return { ...(defaults as object), ...JSON.parse(session) } as T;
  } catch { /* parse error */ }
  return defaults;
}

/** Get last-modified timestamp ISO string for a persisted key (or null). */
export function getPersistedTs(key: string): string | null {
  if (!isBrowser) return null;
  try { return localStorage.getItem(`${key}__ts`); } catch { return null; }
}

/** Subscribe to changes; returns an unsubscribe function. */
export function onPersistChange(handler: (key: string, value: unknown) => void): () => void {
  if (!isBrowser) return () => {};
  const fn = (e: Event) => {
    const ce = e as CustomEvent<{ key: string; value: unknown }>;
    if (ce.detail) handler(ce.detail.key, ce.detail.value);
  };
  window.addEventListener('rb-persist-changed', fn);
  // Also listen for cross-tab storage events
  const storageFn = (e: StorageEvent) => {
    if (e.key && e.newValue) {
      try { handler(e.key, JSON.parse(e.newValue)); } catch { /* ignore */ }
    }
  };
  window.addEventListener('storage', storageFn);
  return () => {
    window.removeEventListener('rb-persist-changed', fn);
    window.removeEventListener('storage', storageFn);
  };
}

/** List all tracked keys (for export / debug). */
export function listAllPersistedKeys(): string[] {
  return getAll();
}

/** Export all persisted data as a single JSON object (for backup). */
export function exportAllPersisted(): Record<string, unknown> {
  const out: Record<string, unknown> = { _exportedAt: new Date().toISOString(), _version: VERSION };
  for (const key of getAll()) {
    try {
      const raw = localStorage.getItem(key);
      if (raw) out[key] = JSON.parse(raw);
    } catch { /* skip */ }
  }
  return out;
}

/** Standard session keys used across the app. */
export const KEYS = {
  DIFFUSER_MODULE:   'rb_diffuser_module_v1',
  CUSTOM_DIFFUSER:   'rb_custom_diffuser_v1',
  CUSTOM_DIFF_EXPORT: 'rb_custom_diffuser_export_v1',
  MODULE_ARRAY:      'rb_module_array_v1',
  BED_RECOMMENDATIONS: 'rb_bed_recommendations_v1',
  RESERVOIR_DESIGN:  'rb_reservoir_design_v1',
} as const;

void STORAGE_PREFIX; // suppress unused warning — namespace reserved for future
